import { Link } from 'react-router-dom';
import { AlertTriangle, Plus, Trophy, Users, Swords, Settings } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

interface AdminPreviewWidgetsProps {
  hasPlayers: boolean;
  hasTournaments: boolean;
  hasMatches: boolean;
}

export function AdminPreviewWidgets({ hasPlayers, hasTournaments, hasMatches }: AdminPreviewWidgetsProps) {
  const suggestions = [];

  if (!hasPlayers) {
    suggestions.push({
      icon: Users,
      title: 'No Players Added',
      description: 'Add players to start tracking stats',
      action: '/admin/players',
      actionLabel: 'Add Players',
    });
  }

  if (!hasTournaments) {
    suggestions.push({
      icon: Trophy,
      title: 'No Tournaments',
      description: 'Create your first tournament',
      action: '/admin/tournaments',
      actionLabel: 'Create Tournament',
    });
  }

  if (!hasMatches) {
    suggestions.push({
      icon: Swords,
      title: 'No Matches Scheduled',
      description: 'Schedule matches to get started',
      action: '/admin/matches',
      actionLabel: 'Add Match',
    });
  }

  if (suggestions.length === 0) return null;

  return (
    <section className="py-6">
      <div className="container">
        <Card className="border-warning/30 bg-warning/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="h-5 w-5 text-warning" />
              <span className="font-display text-sm text-warning">Admin Quick Actions</span>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {suggestions.map((suggestion, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-3 p-3 rounded-xl bg-card/80 border border-border/50"
                >
                  <div className="p-2 rounded-lg bg-warning/10">
                    <suggestion.icon className="h-5 w-5 text-warning" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm text-foreground">{suggestion.title}</div>
                    <div className="text-xs text-muted-foreground truncate">{suggestion.description}</div>
                  </div>
                  <Button asChild size="sm" variant="outline" className="shrink-0">
                    <Link to={suggestion.action}>
                      <Plus className="h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}
